/**
 * Browser calls use same-origin paths prefixed with `REACT_APP_API_BASE` (typically `/api`),
 * mirroring the legacy CRA axios config (`baseURL: process.env.REACT_APP_API_BASE`).
 *
 * Upstream beta (`ewitnessbeta.unit221b.com`) only serves the JSON API under `/api/...`;
 * the slash-less root paths (`/usermanager/...`, `/get_email`) are served as the SPA
 * `index.html` catch-all (200 + text/html), which is why we always prepend the base.
 *
 * In local dev, `npm run dev` runs the Express wrapper that catches `/api/...` and
 * injects `REACT_APP_COOKIE` server-side (see `src/dev-proxy-middleware.cjs`).
 */
function buildApiUrl(path: string): string {
  const raw = (typeof process.env.REACT_APP_API_BASE === "string" ? process.env.REACT_APP_API_BASE : "").trim();
  const base = raw.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!base) return p;
  return `${base}${p}`;
}

function parseApiErrorMessage(text: string): string | null {
  if (/challenges\.cloudflare\.com|Just a moment|cf_chl/i.test(text)) {
    return (
      "Cloudflare challenge blocked the API request. " +
      "Confirm Cloudflare Access/WAF allows the local dev proxy service token."
    );
  }

  try {
    const parsed = JSON.parse(text) as {
      error?: unknown;
      message?: unknown;
      detail?: unknown;
      status?: unknown;
      statusCode?: unknown;
      cloudflare_error?: unknown;
    };

    if (
      parsed.cloudflare_error === true ||
      isWarningHttpStatus(parsed.status ?? parsed.statusCode) ||
      typeof parsed.detail === "string"
    ) {
      return text;
    }
    const msg = parsed.error ?? parsed.message;
    return typeof msg === "string" && msg.length > 0 ? msg : null;
  } catch {
    return null;
  }
}

function isWarningHttpStatus(status: unknown): boolean {
  const code = Number(status);
  return Number.isFinite(code) && [408, 429, 502, 503, 504].includes(code);
}

async function readHttpErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  const fromBody = parseApiErrorMessage(text);
  // Structured JSON payloads (Cloudflare, API timeout)
  if (fromBody?.trim().startsWith("{")) {
    return fromBody;
  }
  // nginx/proxy HTML. Attach status so alerts do not dump the HTML body.
  if (isWarningHttpStatus(res.status) || /<\/?html/i.test(text)) {
    return JSON.stringify({
      status: res.status,
      error: fromBody || res.statusText || `HTTP ${res.status}`,
    });
  }
  return fromBody || text || res.statusText;
}

/**
 * Upstream beta serves the CRA `index.html` as a 200 catch-all when a request is
 * unauthenticated (no redirect, no Set-Cookie). Treat that as a hard auth failure
 * so callers surface a clear error instead of silently normalizing HTML into `[]`.
 * Refresh `REACT_APP_COOKIE` in `.env.local` and restart `npm run dev`.
 */
function isUnauthenticatedHtmlShell(res: Response, text: string): boolean {
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.toLowerCase().includes("text/html")) return false;
  return /<!doctype html/i.test(text);
}

async function parseOkBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  if (isUnauthenticatedHtmlShell(res, text)) {
    throw new Error(
      "Authentication required — upstream returned the app shell (HTML). " +
        "Refresh REACT_APP_COOKIE in .env.local and restart `npm run dev`.",
    );
  }
  const ct = res.headers.get("content-type");
  if (ct?.includes("application/json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

export async function apiGetJson(path: string): Promise<unknown> {
  const url = buildApiUrl(path);
  const res = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error(await readHttpErrorMessage(res));
  }
  return parseOkBody(res);
}

export async function apiPostJson(
  path: string,
  body: unknown,
  init?: RequestInit,
): Promise<unknown> {
  const url = buildApiUrl(path);
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...init?.headers,
    },
    body: JSON.stringify(body ?? {}),
    signal: init?.signal,
  });
  if (!res.ok) {
    throw new Error(await readHttpErrorMessage(res));
  }
  return parseOkBody(res);
}

/**
 * GET returning a Blob (e.g. `/attachment/<sha256>`). This endpoint 302-redirects to a
 * presigned, cross-origin GCS/S3 URL -- fetch() carries the credentials mode through that
 * redirect, and GCS's CORS config has no way to answer with
 * Access-Control-Allow-Credentials: true, so `credentials: "include"` makes the browser
 * reject the redirected response outright (TypeError: Failed to fetch), even with a
 * correct Access-Control-Allow-Origin. "same-origin" still sends the auth cookie on the
 * initial same-origin request to `/api/attachment/...`; it just doesn't try to carry
 * credentials onto the cross-origin GCS leg, which is presigned-URL authenticated anyway.
 */
export async function apiGetBlob(path: string, init?: RequestInit): Promise<Blob> {
  const url = buildApiUrl(path);
  const res = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    ...init,
  });
  if (!res.ok) {
    throw new Error(await readHttpErrorMessage(res));
  }
  return res.blob();
}

export async function apiPatchJson(path: string, body: unknown): Promise<unknown> {
  const url = buildApiUrl(path);
  const res = await fetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    throw new Error(await readHttpErrorMessage(res));
  }
  return parseOkBody(res);
}

export async function apiDeleteJson(path: string, body?: unknown): Promise<unknown> {
  const url = buildApiUrl(path);
  const res = await fetch(url, {
    method: "DELETE",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    throw new Error(await readHttpErrorMessage(res));
  }
  return parseOkBody(res);
}
